import axios, { AxiosError } from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001/api';
const ENDPOINTS = {
  list: `${API_BASE_URL}/customers`,
  byId: (id: string) => `${API_BASE_URL}/customers/${id}`,
  members: (id: string) => `${API_BASE_URL}/customers/${id}/internal-members`,
  member: (id: string, userId: string) => `${API_BASE_URL}/customers/${id}/internal-members/${userId}`,
  projects: (id: string) => `${API_BASE_URL}/customers/${id}/projects`,
  users: (id: string) => `${API_BASE_URL}/customers/${id}/users`,
  user: (id: string, userId: string) => `${API_BASE_URL}/customers/${id}/users/${userId}`,
  invite: (id: string) => `${API_BASE_URL}/customers/${id}/invite`,
  isolationStatus: `${API_BASE_URL}/customers/isolation-status`,
};

export interface Customer {
  id: string;
  company_id: string;
  name: string;
  slug: string;
  description?: string | null;
  logo_url?: string | null;
  industry?: string | null;
  company_size?: string | null;
  website?: string | null;
  phone?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  country?: string | null;
  primary_contact_name?: string | null;
  primary_contact_email?: string | null;
  primary_contact_phone?: string | null;
  secondary_contact_name?: string | null;
  secondary_contact_email?: string | null;
  secondary_contact_phone?: string | null;
  billing_contact_name?: string | null;
  billing_contact_email?: string | null;
  notes?: string | null;
  is_active: boolean;
  is_internal: boolean;
  created_by?: string | null;
  created_at: string;
  updated_at: string;
}

export interface CustomerWithCounts extends Customer {
  project_count: number;
  internal_member_count: number;
  customer_user_count: number;
}

export interface CustomerInternalMember {
  id: string;
  customer_id: string;
  user_id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  assigned_at: string;
}

export interface CustomerProfileFields {
  description?: string | null;
  logo_url?: string | null;
  industry?: string | null;
  company_size?: string | null;
  website?: string | null;
  phone?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  country?: string | null;
  primary_contact_name?: string | null;
  primary_contact_email?: string | null;
  primary_contact_phone?: string | null;
  secondary_contact_name?: string | null;
  secondary_contact_email?: string | null;
  secondary_contact_phone?: string | null;
  billing_contact_name?: string | null;
  billing_contact_email?: string | null;
  notes?: string | null;
}

export interface CreateCustomerInput extends CustomerProfileFields {
  name: string;
  slug?: string;
}

export interface UpdateCustomerInput extends CustomerProfileFields {
  name?: string;
  slug?: string;
}

interface APIError { error: string; }

function handleErr(err: unknown): never {
  if (axios.isAxiosError(err)) {
    const ae = err as AxiosError<APIError>;
    throw new Error(ae.response?.data?.error || ae.message);
  }
  throw err;
}

function authHeaders(token: string) {
  return { headers: { Authorization: `Bearer ${token}` } };
}

export async function listCustomers(token: string): Promise<CustomerWithCounts[]> {
  try {
    const r = await axios.get<{ success: boolean; data: CustomerWithCounts[] }>(ENDPOINTS.list, authHeaders(token));
    return r.data.data;
  } catch (e) { handleErr(e); }
}

export async function getCustomer(id: string, token: string): Promise<Customer> {
  try {
    const r = await axios.get<{ success: boolean; data: Customer }>(ENDPOINTS.byId(id), authHeaders(token));
    return r.data.data;
  } catch (e) { handleErr(e); }
}

export async function createCustomer(data: CreateCustomerInput, token: string): Promise<Customer> {
  try {
    const r = await axios.post<{ success: boolean; data: Customer }>(ENDPOINTS.list, data, authHeaders(token));
    return r.data.data;
  } catch (e) { handleErr(e); }
}

export async function updateCustomer(id: string, data: UpdateCustomerInput, token: string): Promise<Customer> {
  try {
    const r = await axios.put<{ success: boolean; data: Customer }>(ENDPOINTS.byId(id), data, authHeaders(token));
    return r.data.data;
  } catch (e) { handleErr(e); }
}

export async function deleteCustomer(id: string, token: string): Promise<void> {
  try {
    await axios.delete(ENDPOINTS.byId(id), authHeaders(token));
  } catch (e) { handleErr(e); }
}

export interface CustomerProject {
  id: string;
  name: string;
  project_code: string;
  description?: string | null;
  status: string;
  customer_id: string;
  owner_id: string;
  owner_name?: string | null;
  project_type_name?: string | null;
  member_count: number;
  requirement_count: number;
  approved_count: number;
  rejected_count: number;
  is_active: boolean;
  created_at: string;
}

export async function listCustomerProjects(id: string, token: string): Promise<CustomerProject[]> {
  try {
    const r = await axios.get<{ success: boolean; data: CustomerProject[] }>(ENDPOINTS.projects(id), authHeaders(token));
    return r.data.data;
  } catch (e) { handleErr(e); }
}

export interface CustomerUser {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  status: 'active' | 'inactive' | 'pending';
  last_login: string | null;
  created_at: string;
  is_active: boolean;
}

export async function listCustomerUsers(id: string, token: string): Promise<CustomerUser[]> {
  try {
    const r = await axios.get<{ success: boolean; data: CustomerUser[] }>(ENDPOINTS.users(id), authHeaders(token));
    return r.data.data;
  } catch (e) { handleErr(e); }
}

export async function inviteCustomerUser(id: string, email: string, token: string): Promise<CustomerUser> {
  try {
    const r = await axios.post<{ success: boolean; data: CustomerUser }>(ENDPOINTS.invite(id), { email }, authHeaders(token));
    return r.data.data;
  } catch (e) { handleErr(e); }
}

export interface CreateCustomerUserInput {
  email: string;
  first_name: string;
  last_name: string;
  password: string;
}

export async function createCustomerUser(id: string, data: CreateCustomerUserInput, token: string): Promise<CustomerUser> {
  try {
    const r = await axios.post<{ success: boolean; data: CustomerUser }>(ENDPOINTS.users(id), data, authHeaders(token));
    return r.data.data;
  } catch (e) { handleErr(e); }
}

export async function revokeCustomerUser(id: string, userId: string, token: string): Promise<void> {
  try {
    await axios.delete(ENDPOINTS.user(id, userId), authHeaders(token));
  } catch (e) { handleErr(e); }
}

export async function listInternalMembers(id: string, token: string): Promise<CustomerInternalMember[]> {
  try {
    const r = await axios.get<{ success: boolean; data: CustomerInternalMember[] }>(ENDPOINTS.members(id), authHeaders(token));
    return r.data.data;
  } catch (e) { handleErr(e); }
}

export async function assignInternalMember(id: string, userId: string, token: string): Promise<void> {
  try {
    await axios.post(ENDPOINTS.members(id), { user_id: userId }, authHeaders(token));
  } catch (e) { handleErr(e); }
}

export async function unassignInternalMember(id: string, userId: string, token: string): Promise<void> {
  try {
    await axios.delete(ENDPOINTS.member(id, userId), authHeaders(token));
  } catch (e) { handleErr(e); }
}

export async function getIsolationStatus(token: string): Promise<{ customer_isolation_enabled: boolean }> {
  try {
    const r = await axios.get<{ success: boolean; data: { customer_isolation_enabled: boolean } }>(ENDPOINTS.isolationStatus, authHeaders(token));
    return r.data.data;
  } catch (e) { handleErr(e); }
}

export async function updateIsolationStatus(enabled: boolean, token: string): Promise<{ customer_isolation_enabled: boolean }> {
  try {
    const r = await axios.put<{ success: boolean; data: { customer_isolation_enabled: boolean } }>(ENDPOINTS.isolationStatus, { enabled }, authHeaders(token));
    return r.data.data;
  } catch (e) { handleErr(e); }
}

export default {
  listCustomers,
  getCustomer,
  createCustomer,
  updateCustomer,
  deleteCustomer,
  listInternalMembers,
  assignInternalMember,
  unassignInternalMember,
  listCustomerProjects,
  listCustomerUsers,
  inviteCustomerUser,
  createCustomerUser,
  revokeCustomerUser,
  getIsolationStatus,
  updateIsolationStatus,
};
